import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Moves the recommendation strip without moving the purchase controls.
 * @typedef {object} StripRefs
 * @property {HTMLElement} menu - Native, horizontally scrollable product cards.
 * @extends {Component<StripRefs>}
 */
class UpupaSameDayStrip extends Component {
  requiredRefs = ['menu'];
  #abort;
  #mutation;
  #visibility;
  #resize;
  #motion;
  #frame = 0;
  #lastTime = 0;
  #position = 0;
  #direction = 1;
  #interactionTimer;
  #maximum = 0;
  #visible = false;
  #hover = false;
  #focused = false;
  #paused = false;
  #focusTimer;
  #feedbackTimers = new Set();
  #choiceTriggers = new WeakMap();

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    const menu = this.refs.menu;
    this.addEventListener('click', this.#onChoiceClick, options);
    this.addEventListener('change', this.#onChoiceChange, options);
    this.addEventListener('submit', this.#guardChoice, { ...options, capture: true });
    this.addEventListener('close', this.#onChoiceClosed, { ...options, capture: true });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#onCardAdd, options);
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motion.addEventListener('change', () => this.#sync(), options);
    menu.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      this.#hover = true;
      this.#sync();
    }, options);
    menu.addEventListener('pointerleave', () => {
      this.#hover = false;
      this.#sync();
    }, options);
    menu.addEventListener('pointerdown', () => this.#pause(), options);
    menu.addEventListener('wheel', () => this.#pause(), { ...options, passive: true });
    menu.addEventListener('focusin', () => {
      this.#focused = true;
      this.#sync();
    }, options);
    menu.addEventListener('focusout', () => {
      clearTimeout(this.#focusTimer);
      this.#focusTimer = setTimeout(() => {
        this.#focused = menu.contains(document.activeElement);
        this.#sync();
      }, 0);
    }, options);
    document.addEventListener('visibilitychange', () => this.#sync(), options);
    this.#mutation = new MutationObserver(() => this.#refresh());
    this.#mutation.observe(menu, { childList: true });
    this.#resize = new ResizeObserver(() => this.#refresh(false));
    this.#resize.observe(menu);
    this.#visibility = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
      this.#sync();
    }, { threshold: [0, 0.1] });
    this.#visibility.observe(menu);
    this.#refresh();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#mutation?.disconnect();
    this.#resize?.disconnect();
    this.#visibility?.disconnect();
    clearTimeout(this.#focusTimer);
    clearTimeout(this.#interactionTimer);
    for (const timer of this.#feedbackTimers) clearTimeout(timer);
    this.#feedbackTimers.clear();
    this.#stop();
  }

  #onCardAdd = (event) => {
    const form = event.target instanceof Element ? event.target.closest('.upupa-add') : null;
    if (!form || !this.contains(form) || event.action !== 'add' || !event.promise) return;
    const button = form.querySelector('button[type="submit"].upupa-add__btn');
    const label = button?.querySelector('[data-upupa-btn-text]');
    const error = form.querySelector('[data-upupa-add-error]');
    if (!button || !label) return;
    const dialog = form.querySelector('[data-upupa-choice-dialog]');
    const choiceSelect = dialog?.querySelector('[data-upupa-deferred-select]');
    const trigger = dialog && this.#choiceTriggers.get(dialog);
    const triggerLabel = trigger?.querySelector('[data-upupa-choice-trigger-text]');
    clearTimeout(button._upupaTimer);
    this.#feedbackTimers.delete(button._upupaTimer);
    button.classList.remove('is-added');
    button.disabled = true;
    if (choiceSelect) choiceSelect.disabled = true;
    form.setAttribute('aria-busy', 'true');
    label.textContent = form.dataset.addingText;
    if (trigger && triggerLabel) {
      trigger.disabled = true;
      triggerLabel.textContent = form.dataset.addingText;
      trigger.classList.remove('is-added');
    }
    if (error) { error.textContent = ''; error.classList.add('hidden'); }
    const finish = (failed) => {
      if (!this.isConnected || !this.contains(form)) return;
      button.disabled = false;
      if (choiceSelect) choiceSelect.disabled = false;
      form.removeAttribute('aria-busy');
      label.textContent = failed ? form.dataset.addText : form.dataset.addedText;
      if (trigger && triggerLabel) {
        trigger.disabled = false;
        triggerLabel.textContent = failed ? form.dataset.addText : form.dataset.addedText;
      }
      if (failed) {
        if (error) {
          if (!error.textContent.trim()) error.textContent = form.dataset.errorText;
          error.classList.remove('hidden');
        }
        return;
      }
      button.classList.add('is-added');
      trigger?.classList.add('is-added');
      if (dialog?.open) dialog.close();
      const timer = setTimeout(() => {
        this.#feedbackTimers.delete(timer);
        button.classList.remove('is-added');
        label.textContent = form.dataset.addText;
        trigger?.classList.remove('is-added');
        if (triggerLabel) triggerLabel.textContent = form.dataset.addText;
      }, 3000);
      button._upupaTimer = timer;
      this.#feedbackTimers.add(timer);
    };
    event.promise.then((result) => finish(Boolean(result?.detail?.didError)), () => finish(true));
  };

  #onChoiceClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const close = event.target.closest('[data-upupa-choice-close]');
    if (close && this.contains(close)) {
      close.closest('[data-upupa-choice-dialog]')?.close();
      return;
    }
    const backdrop = event.target.matches('[data-upupa-choice-dialog]') ? event.target : null;
    if (backdrop?.open) {
      const rect = backdrop.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) backdrop.close();
      return;
    }
    const trigger = event.target.closest('[data-upupa-choice-trigger]');
    if (!trigger || !this.contains(trigger) || trigger.disabled) return;
    event.preventDefault();
    const card = trigger.closest('.upupa-card');
    const dialog = card?.querySelector('[data-upupa-choice-dialog]');
    const select = dialog?.querySelector('[data-upupa-deferred-select]');
    if (!dialog || !select || typeof dialog.showModal !== 'function') {
      const link = card?.querySelector('[data-upupa-link]');
      if (link) window.location.href = link.href;
      return;
    }
    if (dialog.open) return;
    const submit = dialog.querySelector('[data-upupa-choice-submit]');
    select.value = '';
    if (submit) {
      clearTimeout(submit._upupaTimer);
      this.#feedbackTimers.delete(submit._upupaTimer);
      submit.disabled = true;
      submit.classList.remove('is-added');
      const form = trigger.closest('.upupa-add');
      const label = submit.querySelector('[data-upupa-btn-text]');
      const triggerLabel = trigger.querySelector('[data-upupa-choice-trigger-text]');
      if (label && form) label.textContent = form.dataset.addText;
      if (triggerLabel && form) triggerLabel.textContent = form.dataset.addText;
      trigger.classList.remove('is-added');
    }
    const date = dialog.querySelector('[data-upupa-choice-date]');
    if (date) date.textContent = card.querySelector('[data-upupa-label]')?.value || '';
    const money = dialog.querySelector('[data-upupa-choice-money]');
    if (money) money.textContent = card.querySelector('.p')?.textContent || '';
    const error = dialog.querySelector('[data-upupa-add-error]');
    if (error) { error.textContent = ''; error.classList.add('hidden'); }
    this.#choiceTriggers.set(dialog, trigger);
    trigger.setAttribute('aria-expanded', 'true');
    dialog.showModal();
    select.focus();
  };

  #onChoiceChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-upupa-deferred-select]')) return;
    const select = event.target;
    const dialog = select.closest('[data-upupa-choice-dialog]');
    if (!dialog || !this.contains(dialog)) return;
    const option = select.selectedOptions[0];
    const valid = Boolean(select.value && option && !option.disabled);
    const submit = dialog.querySelector('[data-upupa-choice-submit]');
    if (submit) submit.disabled = !valid;
    const money = dialog.querySelector('[data-upupa-choice-money]');
    if (valid && money) money.textContent = option.dataset.money || '';
  };

  #guardChoice = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const select = event.target.querySelector('[data-upupa-deferred-select]');
    if (!select || (select.value && !select.selectedOptions[0]?.disabled)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    select.focus();
    select.reportValidity();
  };

  #onChoiceClosed = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-upupa-choice-dialog]')) return;
    const trigger = this.#choiceTriggers.get(event.target);
    if (!trigger?.isConnected) return;
    trigger.setAttribute('aria-expanded', 'false');
    trigger.focus({ preventScroll: true });
  };

  /** Toggle the user's persistent pause preference. */
  togglePlayback() {
    this.#paused = !this.#paused;
    this.#sync();
  }

  #pause() {
    this.#paused = true;
    clearTimeout(this.#interactionTimer);
    this.#interactionTimer = setTimeout(() => {
      this.#paused = false;
      this.#sync();
    }, 5000);
    this.#sync();
  }

  #refresh(reset = true) {
    if (!this.isConnected || !this.refs.menu) return;
    if (reset) {
      this.#stop();
      this.refs.menu.scrollLeft = 0;
    }
    this.#maximum = this.refs.menu.scrollWidth - this.refs.menu.clientWidth;
    this.#sync();
  }

  #sync() {
    if (!this.isConnected || !this.refs.menu || !this.#motion) return;
    const { menu } = this.refs;
    const reduced = this.#motion.matches;
    const pairing = this.hasAttribute('data-pairing');
    const running = (pairing || this.#maximum > 1) && !reduced && !this.#paused && !this.#hover &&
      !this.#focused && this.#visible && !document.hidden;
    this.dataset.motion = reduced ? 'reduced' : running ? 'playing' : 'paused';
    this.toggleAttribute('data-running', running);
    if (!running || this.#maximum <= 1) {
      this.#stop();
      return;
    }
    if (this.#frame) return;
    this.#position = menu.scrollLeft;
    this.#lastTime = 0;
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #tick = (time) => {
    this.#frame = 0;
    if (!this.isConnected || !this.hasAttribute('data-running')) return;
    const elapsed = this.#lastTime ? Math.min(time - this.#lastTime, 64) : 0;
    this.#lastTime = time;
    this.#position += elapsed * 0.02 * this.#direction;
    if (this.#position >= this.#maximum) { this.#position = this.#maximum; this.#direction = -1; }
    if (this.#position <= 0) { this.#position = 0; this.#direction = 1; }
    this.refs.menu.scrollLeft = this.#position;
    this.#frame = requestAnimationFrame(this.#tick);
  };

  #stop() {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#lastTime = 0;
  }
}

if (!customElements.get('upupa-same-day-strip')) {
  customElements.define('upupa-same-day-strip', UpupaSameDayStrip);
}

